// file: src/pages/AdminTreatmentOrdersPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Flex,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Link,
  Badge,
  Tooltip,
  HStack,
  Button,
  Menu,
  MenuList,
  MenuButton,
  MenuItem,
  useToast,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Tag,
  TagLabel,
  Collapse,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Switch,
} from "@chakra-ui/react";
import { useCognito } from "../utils/useCognito";
import { SearchIcon } from "@chakra-ui/icons";
import { MdContentCopy, MdDownload, MdOpenInNew, MdDelete, MdRefresh, MdPictureAsPdf } from "react-icons/md";
import getStatusColor from "../utils/getStatusColor";

// ===== ADMIN endpoints (swap when you wire API Gateway) =====
const ADMIN_TREATMENT_ORDERS = "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getTreatmentOrders";
const ADMIN_DELETE_TREATMENT = "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/deleteTreatmentOrder";
const ADMIN_UPDATE_STATUS    = "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/updateTreatmentOrderStatus";
const ADMIN_REGENERATE_PDF   = "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/regenerateTreatmentOrderPdf";
const ADMIN_GET_PDF          = "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getTreatmentOrderPdf";
const ADMIN_CREATE_REORDER   = "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/createTreatmentReorder";

// Keep it super simple - matches backend STATUS constants
const STATUS_VALUES = ["draft", "pending documentation", "submitted", "processing", "completed", "cancelled"];

// ===== Utilities =====
const makePatientKey = (name, dob) =>
  `${(name || "").trim().toUpperCase()}|${(dob || "").trim()}`;

const exportToCsv = (filename, rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return;
  const headers = Array.from(
    rows.reduce((set, r) => {
      Object.keys(r || {}).forEach((k) => set.add(k));
      return set;
    }, new Set())
  );
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (s.includes(",") || s.includes('"') || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const csv = [headers.join(",")]
    .concat(rows.map((r) => headers.map((h) => esc(r[h])).join(",")))
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const StatusPill = ({ status, placement = "top" }) => {
  const letter = (status || "?").charAt(0).toUpperCase();
  const color = getStatusColor(status || "");
  return (
    <Tooltip
      hasArrow
      placement={placement}
      label={
        <Badge colorScheme={color} px={2} py={1}>
          {String(status || "").toUpperCase()}
        </Badge>
      }
      openDelay={100}
    >
      <Badge
        colorScheme={color}
        borderRadius="md"
        px={2}
        fontWeight="bold"
        fontFamily="monospace"
        fontVariantNumeric="tabular-nums"
      >
        {letter}
      </Badge>
    </Tooltip>
  );
};

const PatientDot = ({ id = "", size = 3 }) => {
  const DOT_COLORS = [
    "blue.400",
    "purple.400",
    "teal.400",
    "pink.400",
    "orange.400",
    "cyan.400",
    "red.400",
    "green.400",
    "yellow.500",
    "gray.500",
  ];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  const color = DOT_COLORS[h % DOT_COLORS.length];

  return (
    <Box
      as="span"
      aria-label="patient-color"
      w={`${size * 4}px`}
      h={`${size * 4}px`}
      minW={`${size * 4}px`}
      borderRadius="full"
      bg={color}
      border="1px solid"
      borderColor="blackAlpha.200"
      boxShadow="0 0 0 1px rgba(0,0,0,0.04)"
    />
  );
};

const AdminTreatmentOrdersPage = () => {
  const { getIdToken } = useCognito();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Data
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI state
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Jump-to-Order
  const [searchOrderId, setSearchOrderId] = useState("");
  const initialFocusOrderId = (searchParams.get("orderId") || searchParams.get("focusOrderId") || "").trim();

  // PDF modal state
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  // Regenerate PDF modal
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenMode, setRegenMode] = useState("facility"); // facility | curagenesis
  const [regenLoading, setRegenLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const rowRefs = useRef({});

  // --- API ---
  async function adminGetTreatmentOrders(token, params = {}) {
    // POST to allow complex filters later; backend can accept {status, search}
    const res = await fetch(ADMIN_TREATMENT_ORDERS, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(params),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`Admin getTreatmentOrders failed: ${res.status} ${text}`);
    }
    return res.json();
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = getIdToken();
      const res = await adminGetTreatmentOrders(token, {
        status: statusFilter || undefined,
        search: search?.trim() || undefined,
      });

      const list = Array.isArray(res?.orders) ? res.orders : Array.isArray(res) ? res : [];
      list.sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
      setOrders(list);
    } catch (e) {
      console.error(e);
      toast({
        title: "Failed to load DME orders (admin)",
        description: e?.message || "Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [getIdToken, statusFilter, search, toast]);

  useEffect(() => {
    load();
  }, [load]);

  // Deep link jump once data is present
  useEffect(() => {
    if (!initialFocusOrderId) return;
    const t = setTimeout(() => {
      const el = rowRefs.current[initialFocusOrderId];
      if (el) {
        setExpanded(initialFocusOrderId);
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        // fallback find
        const hit = orders.find(
          (o) => String(o.orderId || o.treatmentOrderId).toLowerCase() === initialFocusOrderId.toLowerCase()
        );
        if (hit) {
          setExpanded(hit.orderId || hit.treatmentOrderId);
          const ref = rowRefs.current[hit.orderId || hit.treatmentOrderId];
          if (ref) ref.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    }, 0);
    return () => clearTimeout(t);
  }, [initialFocusOrderId, orders]);

  // --- Filtering (client-side guardrails in addition to backend) ---
  const matchesSearch = (needle, ...haystacks) => {
    const n = needle.trim().toLowerCase();
    if (!n) return true;
    return haystacks.some((h) => (h || "").toString().toLowerCase().includes(n));
  };

  const filtered = useMemo(() => {
    let list = [...orders];
    if (statusFilter) {
      list = list.filter((o) => (o.status || "").toLowerCase() === statusFilter.toLowerCase());
    }
    if (search) {
      list = list.filter((o) => {
        const orderId = o.orderId || o.treatmentOrderId || "";
        const name = o?.patient?.name || o?.ivr?.patient?.name || "";
        const dob = o?.patient?.dob || o?.ivr?.patient?.dob || "";
        const facility = o?.facility?.name || o?.facilityName || "";
        return matchesSearch(search, orderId, name, dob, facility);
      });
    }
    return list;
  }, [orders, statusFilter, search]);

  // --- Helpers / handlers ---
  const copy = (label, value) => {
    navigator.clipboard.writeText(String(value || ""));
    toast({
      title: "Copied",
      description: `${label} copied to clipboard.`,
      status: "success",
      duration: 1800,
    });
  };

  const refreshSoft = async () => {
    setIsRefreshing(true);
    try {
      await load();
    } finally {
      setIsRefreshing(false);
    }
  };

  const deleteOrder = async (orderId) => {
    if (!orderId) return;
    const confirmed = window.confirm(`Delete treatment order ${orderId}? This cannot be undone.`);
    if (!confirmed) return;

    try {
      const token = await getIdToken();
      const res = await fetch(ADMIN_DELETE_TREATMENT, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ orderId }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Delete failed: ${res.status} ${text}`);
      }
      toast({ title: "Order deleted", status: "success", duration: 2500, isClosable: true });
      refreshSoft();
    } catch (err) {
      console.error(err);
      toast({
        title: "Failed to delete order",
        description: err?.message || "Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const updateStatus = async (orderId, status) => {
    try {
      const token = await getIdToken();
      const res = await fetch(ADMIN_UPDATE_STATUS, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ orderId, status }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || `Status update failed: ${res.status}`);
      }
      toast({ title: `Status set to ${status}`, status: "success", duration: 2500, isClosable: true });
      refreshSoft();
    } catch (err) {
      console.error(err);
      toast({
        title: "Failed to update status",
        description: err?.message || "Please try again.",
        status: "error",
        duration: 5000,
      });
    }
  };

  const viewPdf = async (orderId) => {
    try {
      const token = await getIdToken();
      const res = await fetch(ADMIN_GET_PDF, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ orderId }),
      });
      const data = await res.json();
      if (res.ok && data?.pdfUrl) {
        setPdfUrl(data.pdfUrl);
        setPdfModalOpen(true);
      } else {
        throw new Error(data?.message || "No PDF available");
      }
    } catch (e) {
      console.error(e);
      toast({
        title: "Could not fetch PDF",
        description: e?.message || "Try regenerating first.",
        status: "warning",
      });
    }
  };

  const regeneratePdf = async (order) => {
    if (!order) return;
    setSelectedOrder(order);
    setRegenMode("facility");
    setRegenOpen(true);
  };

  const doRegeneratePdf = async () => {
    if (!selectedOrder) return;
    setRegenLoading(true);
    try {
      const token = await getIdToken();
      const res = await fetch(ADMIN_REGENERATE_PDF, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          orderId: selectedOrder.orderId || selectedOrder.treatmentOrderId,
          mode: regenMode, // "facility" | "curagenesis"
        }),
      });
      const data = await res.json();
      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || `Regenerate failed: ${res.status}`);
      }
      toast({
        title: "PDF regenerated",
        description: `Mode: ${regenMode}`,
        status: "success",
        duration: 3000,
      });
      setRegenOpen(false);
      refreshSoft();
    } catch (e) {
      console.error(e);
      toast({
        title: "Regenerate failed",
        description: e?.message || "Please check logs.",
        status: "error",
        duration: 5000,
      });
    } finally {
      setRegenLoading(false);
    }
  };

  const createReorder = async (order) => {
    const orderId = order?.orderId || order?.treatmentOrderId;
    if (!orderId) return;
    try {
      const token = await getIdToken();
      const res = await fetch(ADMIN_CREATE_REORDER, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ orderId }),
      });
      const data = await res.json();
      if (!res.ok || !data?.newOrderId) {
        throw new Error(data?.message || `Reorder failed: ${res.status}`);
      }
      toast({
        title: "Reorder created",
        description: `New Order ID: ${data.newOrderId}`,
        status: "success",
      });
      // Jump to the new admin edit flow if desired:
      // navigate(`/admin/create-treatment-order?cloneOrderId=${encodeURIComponent(data.newOrderId)}`);
      refreshSoft();
    } catch (e) {
      console.error(e);
      toast({
        title: "Reorder failed",
        description: e?.message || "Please review server logs.",
        status: "error",
      });
    }
  };

  const handleJump = () => {
    const id = searchOrderId.trim();
    if (!id) {
      toast({ title: "Enter an Order ID", status: "info", duration: 1500, isClosable: true });
      return;
    }
    const hit = filtered.find(
      (o) => String(o.orderId || o.treatmentOrderId).toLowerCase() === id.toLowerCase()
    );
    if (!hit) {
      toast({ title: "Order not found", description: id, status: "warning", duration: 2500, isClosable: true });
      return;
    }
    const key = hit.orderId || hit.treatmentOrderId;
    setExpanded(key);
    requestAnimationFrame(() => {
      const el = rowRefs.current[key];
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  // --- UI ---
  if (loading) {
    return (
      <Box p={8}>
        <Flex minH="60vh" align="center" justify="center" gap={3}>
          <Spinner size="xl" />
          <Text fontSize="lg">Loading Treatment Orders…</Text>
        </Flex>
      </Box>
    );
  }

  const statPatients = new Set(
    filtered.map((o) =>
      makePatientKey(o?.patient?.name || o?.ivr?.patient?.name, o?.patient?.dob || o?.ivr?.patient?.dob)
    )
  ).size;

  return (
    <Box p={[4, 6, 8]}>
      <Flex justify="space-between" align="center" mb={3} wrap="wrap" gap={3}>
        <Text fontSize="2xl" fontWeight="bold">Admin DME Orders</Text>
        <HStack>
          <Input
            size="sm"
            maxW="360px"
            placeholder="Jump to Order ID…"
            value={searchOrderId}
            onChange={(e) => setSearchOrderId(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleJump()}
          />
          <Button size="sm" onClick={handleJump} _hover={{ transform: "scale(1)" }}>Go</Button>
          <Button
            size="sm"
            variant="outline"
            leftIcon={<MdRefresh />}
            onClick={refreshSoft}
            _hover={{ transform: "scale(1)" }}
            isDisabled={isRefreshing}
          >
            {isRefreshing ? "Refreshing…" : "Refresh"}
          </Button>
        </HStack>
      </Flex>

      {/* Controls */}
      <Flex
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        direction={{ base: "column", md: "row" }}
        gap={3}
        mb={4}
      >
        <HStack spacing={2}>
          <InputGroup size="sm" w={{ base: "100%", md: "320px" }} maxW="360px" flexShrink={0}>
            <InputLeftElement pointerEvents="none" h="full" w="2.25rem">
              <SearchIcon boxSize={4} color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search Order, Patient, DOB, Facility"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              bg="white"
              pl="2.25rem"
              paddingInlineStart="2.25rem"
              sx={{ paddingLeft: "2rem !important" }}
            />
          </InputGroup>

          <Menu>
            <MenuButton as={Button} size="sm" width="200px" _hover={{ transform: "scale(1)" }}>
              {statusFilter ? (
                <Badge colorScheme={getStatusColor(statusFilter)} px={2} py={1}>
                  {statusFilter.toUpperCase()}
                </Badge>
              ) : (
                "Filter by Status"
              )}
            </MenuButton>
            <MenuList>
              <MenuItem onClick={() => setStatusFilter("")}>All</MenuItem>
              {STATUS_VALUES.map((s) => (
                <MenuItem key={s} onClick={() => setStatusFilter(s)} p={0}>
                  <Box width="100%" p={2} bg="white" border="1px solid" borderColor="gray.200">
                    <Badge colorScheme={getStatusColor(s)} px={2} py={1}>
                      {s.toUpperCase()}
                    </Badge>
                  </Box>
                </MenuItem>
              ))}
            </MenuList>
          </Menu>

          <Tooltip label="Export current list to CSV">
            <IconButton
              aria-label="Export CSV"
              icon={<MdDownload />}
              size="sm"
              onClick={() => {
                const rows = filtered.map((o) => ({
                  orderId: o.orderId || o.treatmentOrderId || "",
                  patientName: o?.patient?.name || o?.ivr?.patient?.name || "",
                  patientDob: o?.patient?.dob || o?.ivr?.patient?.dob || "",
                  facility: o?.facility?.name || o?.facilityName || "",
                  status: o.status || "",
                  createdAt: o.createdAt || "",
                  updatedAt: o.updatedAt || "",
                }));
                exportToCsv("admin_treatment_orders.csv", rows);
              }}
              _hover={{ transform: "scale(1)" }}
            />
          </Tooltip>
        </HStack>

        <HStack>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSearch("");
              setStatusFilter("");
              refreshSoft();
            }}
            _hover={{ transform: "scale(1)" }}
          >
            Clear
          </Button>
        </HStack>
      </Flex>

      {/* Stats */}
      <SimpleGrid columns={{ base: 1, sm: 3 }} gap={4} mb={4}>
        <Stat bg="white" borderRadius="lg" p={4} boxShadow="sm">
          <StatLabel>Total Orders</StatLabel>
          <StatNumber>{filtered.length}</StatNumber>
          <StatHelpText>Filtered view</StatHelpText>
        </Stat>
        <Stat bg="white" borderRadius="lg" p={4} boxShadow="sm">
          <StatLabel>Patients (approx)</StatLabel>
          <StatNumber>{statPatients}</StatNumber>
          <StatHelpText>Name + DOB</StatHelpText>
        </Stat>
        <Stat bg="white" borderRadius="lg" p={4} boxShadow="sm">
          <StatLabel>Statuses Present</StatLabel>
          <StatNumber>{new Set(filtered.map((o) => o.status || "")).size}</StatNumber>
          <StatHelpText>Unique</StatHelpText>
        </Stat>
      </SimpleGrid>

      {/* Table */}
      {filtered.length === 0 ? (
        <Box
          mt={8}
          p={8}
          bg="white"
          borderRadius="xl"
          border="1px solid"
          borderColor="gray.100"
          textAlign="center"
        >
          <Text fontSize="lg" fontWeight="semibold" mb={2}>
            No DME Orders found
          </Text>
          <Text color="gray.600">Try adjusting your filters.</Text>
        </Box>
      ) : (
        <Table variant="simple" size="md">
          <Thead position="sticky" top={0} zIndex={1} bg="gray.50">
            <Tr>
              <Th>Identifier</Th>
              <Th>Patient</Th>
              <Th>Facility</Th>
              <Th>Status</Th>
              <Th>Updated</Th>
              <Th>Created</Th>
              <Th>Primary</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filtered.map((order) => {
              const orderId = order.orderId || order.treatmentOrderId || "";
              const status = order.status || "";
              const updatedAt = order.updatedAt || "—";
              const createdAt = order.createdAt || "—";
              const pName = order?.patient?.name || order?.ivr?.patient?.name || "—";
              const pDob = order?.patient?.dob || order?.ivr?.patient?.dob || "";
              const facility = order?.facility?.name || order?.facilityName || "—";
              const pKey = makePatientKey(pName, pDob);
              const isExpanded = expanded === orderId;

              return (
                <React.Fragment key={orderId}>
                  <Tr
                    onClick={() => setExpanded(isExpanded ? null : orderId)}
                    cursor="pointer"
                    ref={(el) => { if (el) rowRefs.current[orderId] = el; }}
                  >
                    <Td>
                      <HStack spacing={2}>
                        <Text fontSize="md" color="gray.500">{isExpanded ? "▴" : "▾"}</Text>
                        <Text fontFamily="mono" fontVariantNumeric="tabular-nums">{orderId}</Text>
                        <Tooltip label="Copy Order ID">
                          <IconButton
                            aria-label="Copy Order ID"
                            icon={<MdContentCopy />}
                            size="xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              copy("Order ID", orderId);
                            }}
                            _hover={{ borderColor: "var(--primary-color)", borderWidth: "1px" }}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <PatientDot id={pKey} size={3} />
                        <Text>{pName}</Text>
                        {pDob && (
                          <Tag size="sm" colorScheme="gray" borderRadius="full">
                            <TagLabel>{pDob}</TagLabel>
                          </Tag>
                        )}
                      </HStack>
                    </Td>
                    <Td>{facility}</Td>
                    <Td><StatusPill status={status} placement="top" /></Td>
                    <Td>{updatedAt ? new Date(updatedAt).toLocaleString() : "—"}</Td>
                    <Td>{createdAt ? new Date(createdAt).toLocaleString() : "—"}</Td>
                    <Td>
                      {status === "draft" ? (
                        <Link
                          as={RouterLink}
                          to={`/admin/create-treatment-order?orderId=${encodeURIComponent(orderId)}`}
                          color="green.600"
                        >
                          Resume Editing
                        </Link>
                      ) : status === "pending documentation" ? (
                        <Link
                          as={RouterLink}
                          to={`/admin/create-treatment-order?orderId=${encodeURIComponent(orderId)}`}
                          color="orange.600"
                        >
                          Add Documents
                        </Link>
                      ) : (
                        <Link
                          as={RouterLink}
                          to={`/admin/treatment/${encodeURIComponent(orderId)}`}
                          color="blue.600"
                        >
                          View Details
                        </Link>
                      )}
                    </Td>
                  </Tr>

                  <Tr>
                    <Td colSpan={7} p={0} borderTop="none">
                      <Collapse in={isExpanded} animateOpacity>
                        <Box p={4} bg="gray.50" borderBottom="1px solid #ccc">
                          {/* Admin Actions */}
                          <HStack spacing={4} wrap="wrap">
                            {/* Status quick-set */}
                            <Menu>
                              <MenuButton as={Button} size="sm" variant="outline" _hover={{ transform: "scale(1)" }}>
                                Set Status
                              </MenuButton>
                              <MenuList>
                                {STATUS_VALUES.map((s) => (
                                  <MenuItem
                                    key={s}
                                    onClick={() => updateStatus(orderId, s)}
                                  >
                                    <Badge colorScheme={getStatusColor(s)} mr={2}>{s.toUpperCase()}</Badge>
                                  </MenuItem>
                                ))}
                              </MenuList>
                            </Menu>

                            {/* View PDF */}
                            <Button
                              size="sm"
                              leftIcon={<MdPictureAsPdf />}
                              variant="outline"
                              onClick={() => viewPdf(orderId)}
                              _hover={{ transform: "scale(1)" }}
                            >
                              View PDF
                            </Button>

                            {/* Regenerate PDF */}
                            <Button
                              size="sm"
                              leftIcon={<MdRefresh />}
                              variant="outline"
                              onClick={() => regeneratePdf(order)}
                              _hover={{ transform: "scale(1)" }}
                            >
                              Regenerate PDF
                            </Button>

                            {/* Reorder */}
                            <Button
                              size="sm"
                              colorScheme="teal"
                              onClick={() => createReorder(order)}
                              _hover={{ transform: "scale(1)" }}
                            >
                              Create Reorder
                            </Button>

                            {/* Admin edit flow */}
                            <Link
                              as={RouterLink}
                              to={`/admin/treatment/${encodeURIComponent(orderId)}`}
                              color="blue.600"
                            >
                              Full Admin View
                            </Link>

                            {/* Delete (usually drafts only, but leaving flexible) */}
                            <Button
                              size="sm"
                              colorScheme="red"
                              leftIcon={<MdDelete />}
                              variant="outline"
                              onClick={() => deleteOrder(orderId)}
                              _hover={{ transform: "scale(1)" }}
                            >
                              Delete
                            </Button>
                          </HStack>

                          {/* Quick meta panel */}
                          <Box mt={3} color="gray.700" fontSize="sm">
                            <Text>
                              <strong>Patient:</strong> {pName} {pDob ? `• DOB: ${pDob}` : ""}
                            </Text>
                            <Text><strong>Facility:</strong> {facility}</Text>
                            <Text><strong>Status:</strong> {status}</Text>
                          </Box>
                        </Box>
                      </Collapse>
                    </Td>
                  </Tr>
                </React.Fragment>
              );
            })}
          </Tbody>
        </Table>
      )}

      {/* PDF Modal (simple native viewer in a new tab) */}
      <Modal isOpen={pdfModalOpen} onClose={() => { setPdfModalOpen(false); setPdfUrl(null); }}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Order PDF</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {pdfUrl ? (
              <Link href={pdfUrl} isExternal color="blue.600">
                Open PDF in new tab <MdOpenInNew style={{ display: "inline-block", marginLeft: 4 }} />
              </Link>
            ) : (
              <Text>No PDF available.</Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => { setPdfModalOpen(false); setPdfUrl(null); }} _hover={{ transform: "scale(1)" }}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Regenerate PDF Modal */}
      <Modal isOpen={regenOpen} onClose={() => setRegenOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Regenerate Treatment Order PDF</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={3}>
              Regenerate PDF for Order:{" "}
              <strong>{selectedOrder?.orderId || selectedOrder?.treatmentOrderId}</strong>
            </Text>
            <HStack spacing={4} mb={2}>
              <Text fontSize="sm" fontWeight="medium">PDF Format:</Text>
              <HStack>
                <Text fontSize="sm">Physician Facility</Text>
                <Switch
                  isChecked={regenMode === "curagenesis"}
                  onChange={(e) => setRegenMode(e.target.checked ? "curagenesis" : "facility")}
                  colorScheme="blue"
                />
                <Text fontSize="sm">CuraGenesis Office</Text>
              </HStack>
            </HStack>
            <Text fontSize="sm" color="gray.600">
              Choose the formatting style for the regenerated PDF document.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setRegenOpen(false)} isDisabled={regenLoading}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={doRegeneratePdf}
              isLoading={regenLoading}
              loadingText="Regenerating..."
              _hover={{ transform: "scale(1)" }}
            >
              Regenerate PDF ({regenMode})
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AdminTreatmentOrdersPage;
